import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { NotPermitted } from '@/design';
import { ApiError, ProblemType } from '@/domains/api';
import { TestInboxField, useCan, useTestInbox, type AccountKind } from '@/domains/auth';
import { accountsApi } from '../api/accounts-api';
import { ACCOUNT_KINDS, type ICreateAccount } from '../api/account-contracts';
import type { IRole } from '../api/role-contracts';
import { accountKindWords } from '../components/account-facts';
import { RoleCheckbox, useRoleList } from '../components/account-roles';
import { LoadError } from '../components/admin-page';
import { namesSomethingGone, roleModelRefusal, statusRefusal } from '../refusal';

/** `POST /accounts`' 403, in the words both of its callers use — the create card, and the
 *  person hub's new seat. Without `roles.manage` a caller may only ask for an advisor. */
export const CREATE_ACCOUNT_FORBIDDEN =
  'You may only create Advisor Portal accounts — a Team Portal account needs a role that ' +
  'manages roles.';

/** `POST /accounts`' 409 `idp-address-taken` (SR-476), in the words both of its callers use. */
export const IDP_ADDRESS_TAKEN =
  'Another account’s live invitation or identity holds this address, so the seat would have ' +
  'no way to sign in. Nothing was created.';

/**
 * `POST /accounts` — provisioning a seat.
 *
 * The seat signs in through Invent's IdP (SR-476): survey-api queues its invitation, and its
 * worker asks the IdP and mails the redeem link. The route answers 201 with no body. What the card
 * says is where the invitation goes and what to do when it does not arrive: the account's
 * Sign-in card shows the redeem link once the IdP made it, for staff to hand over.
 *
 * ## The test inbox
 *
 * Creating a seat causes a mail, so where the deployment holds its mail to an allowlist — dev
 * and UAT — the card draws the test inbox (`TestInboxField`), and the invitation goes there
 * instead of to the new address. See `useTestInbox`.
 *
 * ## Who may create what (SR-228)
 *
 * The route is `accounts.manage`, and the Users screen opens on `accounts.browse` — so a reader
 * without the first sees the card with its controls off and the reason. A Team Portal seat asks
 * `roles.manage` besides (`CreateAccountService`): an actor could otherwise mint a staff seat at an
 * address it controls and sign in as it, escalating itself in one call. That choice is drawn off
 * for a reader without it, with the reason, rather than left to fail: a 403 on submit would be
 * correct and useless.
 *
 * The body is `{ email, kind, roleIds?, deliverTo? }`: the kind first, then — for a Team Portal
 * seat — the roles it starts with, read from `GET /admin/roles`. None is allowed, and said: such
 * a seat opens nothing but Team Home until it is given a role. Whether the caller may give a role
 * holding a permission it lacks is survey-api's `permission-not-held` (D5), said under the form.
 */
export function CreateAccountCard({ onCreated }: { onCreated: () => void }) {
  const can = useCan();
  // The route is `accounts.manage`; a Team Portal seat asks `roles.manage` besides (SR-228).
  const canCreate = can('accounts.manage');
  const canGiveAuthority = canCreate && can('roles.manage');
  const reasonId = React.useId();

  const [email, setEmail] = React.useState('');
  const [kind, setKind] = React.useState<AccountKind>('advisor');
  const [roleIds, setRoleIds] = React.useState<readonly string[]>([]);
  const roleList = useRoleList(canGiveAuthority);
  const roles = roleList.data?.roles ?? null;
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<Nullable<string>>(null);
  const [created, setCreated] = React.useState<Nullable<ICreateAccount>>(null);
  const inbox = useTestInbox();

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setCreated(null);
    inbox.begin();
    const request: ICreateAccount = {
      email: email.trim(),
      kind,
      ...(kind === 'staff' ? { roleIds: [...roleIds] } : {}),
      ...inbox.body
    };
    try {
      await accountsApi.createAccount(request);
      setCreated(request);
      setEmail('');
      setRoleIds([]);
      onCreated();
    } catch (createError) {
      if (inbox.refused(createError, request.email)) return;
      // A role chosen that survey-api no longer holds: the boxes are read again, as said.
      if (namesSomethingGone(createError)) void roleList.reload();
      if (createError instanceof ApiError && createError.isProblem(ProblemType.idpAddressTaken)) {
        setError(IDP_ADDRESS_TAKEN);
        return;
      }
      setError(
        roleModelRefusal(createError) ??
          statusRefusal(
            createError,
            {
              409: 'That address already has an account. Change it from its own page instead.',
              403: CREATE_ACCOUNT_FORBIDDEN
            },
            'Could not create this account'
          )
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Create an account</CardTitle>
        <CardDescription>
          Reserves the address and invites its owner to sign in at Invent.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <Alert>
          <AlertTitle>The invitation is mailed</AlertTitle>
          <AlertDescription>
            If the mail does not arrive, the account’s Sign-in card shows its redeem link once
            Invent has made it — hand it to the holder of the address yourself.
          </AlertDescription>
        </Alert>

        <form className="grid gap-3" onSubmit={submit}>
          <div className="flex flex-wrap items-start gap-3">
            <Input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="name@firm.com"
              aria-label="New account email"
              autoComplete="off"
              className="max-w-xs"
              disabled={!canCreate}
              aria-describedby={canCreate ? undefined : reasonId}
            />
            <Select
              value={kind}
              onValueChange={(next) => setKind(next as AccountKind)}
              disabled={!canCreate}
            >
              <SelectTrigger
                className="w-56"
                aria-label="Kind of the new account"
                aria-describedby={canCreate && !canGiveAuthority ? reasonId : undefined}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ACCOUNT_KINDS.map((candidate) => (
                  <SelectItem
                    key={candidate}
                    value={candidate}
                    disabled={candidate === 'staff' && !canGiveAuthority}
                  >
                    {accountKindWords(candidate)} account
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="submit"
              disabled={busy || !canCreate || !inbox.settled || email.trim() === ''}
              aria-describedby={canCreate ? undefined : reasonId}
            >
              {busy ? 'Creating…' : 'Create'}
            </Button>
          </div>
          {!canCreate ? (
            <NotPermitted id={reasonId} what="creating accounts" />
          ) : (
            !canGiveAuthority && (
              <NotPermitted id={reasonId} what="creating a Team Portal account" />
            )
          )}

          {kind === 'staff' && canGiveAuthority && (
            <NewAccountRoles
              roles={roles}
              error={roleList.error}
              onReadAgain={() => void roleList.reload()}
              chosen={roleIds}
              onChoose={setRoleIds}
              disabled={busy}
            />
          )}

          <TestInboxField
            inbox={inbox}
            id="new-account-deliver-to"
            intended={email}
            disabled={busy || !canCreate}
          />
        </form>

        {error && (
          <Alert variant="destructive">
            <AlertTitle>Not created</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {created && !error && (
          <p className="text-sm text-muted-foreground">
            Created <span className="font-medium">{created.email}</span>. Its invitation to sign
            in goes to <span className="font-medium">{created.deliverTo ?? 'that address'}</span>
            {created.deliverTo && ' (the test inbox)'}.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/** The roles a new Team Portal seat starts with — every role a checkbox, none required. */
function NewAccountRoles({
  roles,
  error,
  onReadAgain,
  chosen,
  onChoose,
  disabled
}: {
  roles: Nullable<IRole[]>;
  error: Nullable<string>;
  onReadAgain: () => void;
  chosen: readonly string[];
  onChoose: (next: readonly string[]) => void;
  disabled: boolean;
}) {
  const idPrefix = React.useId();
  return (
    <fieldset className="grid gap-2" data-new-account-roles>
      <legend className="mb-1 text-sm font-medium">Team Portal roles to give it</legend>
      {roles === null ? (
        error ? (
          <div className="grid justify-items-start gap-2">
            <LoadError message={error} />
            <Button type="button" variant="outline" size="sm" onClick={onReadAgain}>
              Read again
            </Button>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Reading the roles…</p>
        )
      ) : (
        roles.map((role) => (
          <RoleCheckbox
            key={role.roleId}
            id={`${idPrefix}-${role.roleId}`}
            name={role.name}
            description={role.description}
            checked={chosen.includes(role.roleId)}
            disabled={disabled}
            onCheckedChange={(on) =>
              onChoose(
                on ? [...chosen, role.roleId] : chosen.filter((roleId) => roleId !== role.roleId)
              )
            }
          />
        ))
      )}
      {chosen.length === 0 && (
        <p className="text-sm text-muted-foreground">
          With no role it opens nothing but Team Home until it is given one on its own page.
        </p>
      )}
    </fieldset>
  );
}
